import { useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '../../@types/ApiResponse';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getJSON, type RequestError } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import { validateRegistraionOrVin } from '../../utils/vehicle';
import useMarketCode from '../branch/useMarketCode';
import type { RiskAssessmentReport, ValidatedOptions } from './useVehicle';

export interface EnhancedPropertiesSubCategory {
  id: number;
  name: string;
}

export interface EnhancedPropertiesCategory {
  id: number;
  name: string;
  subCategory?: EnhancedPropertiesSubCategory;
}

export interface EnhancedProperties {
  category: EnhancedPropertiesCategory;
  name: string;
  unit: string | null;
  value: boolean | string | number | string[];
}

export interface ColorCode {
  code: string;
  name: string;
}

export interface RetailPrice {
  to: number;
  from: number;
}

export interface DetailedVehicle {
  colorCodes: ColorCode[];
  drivingWheel: string;
  enginePower: number;
  enhancedProperties: { [key: string]: EnhancedProperties | undefined };
  equipmentLevel: string | null;
  fuelType: string;
  gearbox: string;
  gearboxType: string;
  isAssociatedWithRisk: boolean;
  manufactureYear: number;
  manufacturer: string;
  mileage: number;
  modelName: string;
  modelSeries: string;
  modelYear: number;
  options: string[] | null;
  properties: { [key: string]: string | boolean | number | null | undefined };
  rawResellerInformation: null;
  registrationDate: string;
  registrationNumber: string;
  retailPrices: RetailPrice;
  salesName: string;
  segment: string;
  vehicleType: string;
  vin: string;
  validatedOptions: ValidatedOptions | null;
  riskAssessmentReport: RiskAssessmentReport;
}

export const DETAILED_VEHICLE_QUERY = '/api/vehicle/:regnr/detailed';

interface DetailedError {
  error: { message: string };
}

/**
 * Query function for fetching detailed vehicle info.
 * Can be used with queryClient.fetchQuery for imperative fetching.
 */
export const detailedVehicleQueryFn = async (
  marketCode: string,
  registrationNumber: string,
  token: string,
): Promise<ApiResponse<DetailedVehicle>> =>
  await getJSON<ApiResponse<DetailedVehicle>>(
    `/api/vehicle/${marketCode}/${registrationNumber}/detailed`,
    token,
  );

const useDetailedVehicle = (
  value?: string,
): [DetailedVehicle | undefined, RequestStateQuery<DetailedError>] => {
  const { getToken } = useAuth();
  const [marketCode] = useMarketCode();

  const validValue = validateRegistraionOrVin(value, marketCode);

  const { data, isLoading, isSuccess, isError, error } = useQuery<
    ApiResponse<DetailedVehicle> | undefined
  >({
    queryKey: [DETAILED_VEHICLE_QUERY, value],
    queryFn: async () =>
      await getJSON<ApiResponse<DetailedVehicle>>(
        `/api/vehicle/${marketCode}/${value}/detailed`,
        getToken()!,
      ),
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: validValue && !!marketCode,
  });

  return [
    data?.response,
    {
      isLoading,
      isSuccess,
      isError,
      error: error as unknown as RequestError<DetailedError>,
    },
  ];
};

export default useDetailedVehicle;
